import { Type } from '@angular/core';

import { HeroSectionComponent } from '../../shared/organisms/hero-section/hero-section';
import { adaptHeroSection } from '../../shared/organisms/hero-section/hero-section.adapter';
import { DynamicSectionComponent } from '../../shared/organisms/dynamic-section/dynamic-section';
import { adaptDynamicSection } from '../../shared/organisms/dynamic-section/dynamic-section.adapter';

// Block-level components (rendered inside sections by DynamicSectionComponent)
import { CardsBlockComponent } from '../../shared/organisms/cards-block/cards-block';
import { adaptCardsBlock } from '../../shared/organisms/cards-block/cards-block.adapter';
import { TextBlockComponent } from '../../shared/organisms/text-block/text-block';
import { adaptTextBlock } from '../../shared/organisms/text-block/text-block.adapter';
import { CtaBlockComponent } from '../../shared/organisms/cta-block/cta-block';
import { adaptCtaBlock } from '../../shared/organisms/cta-block/cta-block.adapter';
import { TableBlockComponent } from '../../shared/organisms/table-block/table-block';
import { adaptTableBlock } from '../../shared/organisms/table-block/table-block.adapter';
import { CalloutBlockComponent } from '../../shared/organisms/callout-block/callout-block';
import { adaptCalloutBlock } from '../../shared/organisms/callout-block/callout-block.adapter';
import { StatsBlockComponent } from '../../shared/organisms/stats-block/stats-block';
import { adaptStatsBlock } from '../../shared/organisms/stats-block/stats-block.adapter';
import { FaqBlockComponent } from '../../shared/organisms/faq-block/faq-block';
import { adaptFaqBlock } from '../../shared/organisms/faq-block/faq-block.adapter';
import { LogoSectionComponent } from '../../shared/organisms/logo-section/logo-section';
import { adaptLogoSection } from '../../shared/organisms/logo-section/logo-section.adapter';
import { ArchiveSectionComponent } from '../../shared/organisms/archive-section/archive-section';
import { adaptArchiveSection } from '../../shared/organisms/archive-section/archive-section.adapter';
import { QuoteBlockComponent } from '../../shared/molecules/quote-block/quote-block';
import { adaptQuoteBlock } from '../../shared/molecules/quote-block/quote-block.adapter';
import { LogoBlockComponent } from '../../shared/organisms/logo-block/logo-block';
import { adaptLogoBlock } from '../../shared/organisms/logo-block/logo-block.adapter';
import { TimelineBlockComponent } from '../../shared/organisms/timeline-block/timeline-block';
import { adaptTimelineBlock } from '../../shared/organisms/timeline-block/timeline-block.adapter';
import { StepBlockComponent } from '../../shared/organisms/step-block/step-block';
import { adaptStepBlock } from '../../shared/organisms/step-block/step-block.adapter';
import { TabBlockComponent } from '../../shared/organisms/tab-block/tab-block';
import { adaptTabBlock } from '../../shared/organisms/tab-block/tab-block.adapter';
import { InsightsCtaBlockComponent } from '../../shared/organisms/insights-cta-block/insights-cta-block';
import { adaptInsightsCtaBlock } from '../../shared/organisms/insights-cta-block/insights-cta-block.adapter';
import { DetectionSummaryBlockComponent } from '../../shared/molecules/detection-summary-block/detection-summary-block';
import { adaptDetectionSummaryBlock } from '../../shared/molecules/detection-summary-block/detection-summary-block.adapter';
import { VideoEmbedBlockComponent } from '../../shared/organisms/video-embed-block/video-embed-block';
import { adaptVideoEmbedBlock } from '../../shared/organisms/video-embed-block/video-embed-block.adapter';
import { DrivetrainSchematicBlockComponent } from '../../shared/molecules/drivetrain-schematic-block/drivetrain-schematic-block';
import { adaptDrivetrainSchematicBlock } from '../../shared/molecules/drivetrain-schematic-block/drivetrain-schematic-block.adapter';
import { RawHtmlBlockComponent } from '../../shared/organisms/raw-html-block/raw-html-block';
import { adaptRawHtmlBlock } from '../../shared/organisms/raw-html-block/raw-html-block.adapter';
import { HubSpotFormBlockComponent } from '../../shared/molecules/hubspot-form-block/hubspot-form-block';
import { adaptHubSpotFormBlock } from '../../shared/molecules/hubspot-form-block/hubspot-form-block.adapter';

export interface DzRegistryEntry {
  component: Type<unknown>;
  adapter: (entry: Record<string, unknown>, baseUrl: string) => Record<string, unknown>;
}

export const COMPONENT_REGISTRY: Record<string, DzRegistryEntry> = {
  // === Section-level: hero is special (full-width, no inner blocks) ===
  'sections.hero-section': {
    component: HeroSectionComponent,
    adapter: adaptHeroSection,
  },

  // === Section-level: all others use DynamicSectionComponent as wrapper ===
  // The unified adapter extracts heading/subtitle/bg and flattens
  // cardsBlock/ctaBlock/textBlock/blocks[] into a blocks array.
  'sections.dynamic-section': { component: DynamicSectionComponent, adapter: adaptDynamicSection },
  'sections.cards-section': { component: DynamicSectionComponent, adapter: adaptDynamicSection },
  'sections.text-section': { component: DynamicSectionComponent, adapter: adaptDynamicSection },
  'sections.cta-section': { component: DynamicSectionComponent, adapter: adaptDynamicSection },
  'sections.related-section': { component: DynamicSectionComponent, adapter: adaptDynamicSection },
  'sections.faq-section': { component: DynamicSectionComponent, adapter: adaptDynamicSection },
  'sections.logo-section': { component: LogoSectionComponent, adapter: adaptLogoSection },
  'sections.archive-section': { component: ArchiveSectionComponent, adapter: adaptArchiveSection },

  // === Block-level: rendered inside DynamicSectionComponent ===
  'blocks.cards-block': { component: CardsBlockComponent, adapter: adaptCardsBlock },
  'blocks.text-block': { component: TextBlockComponent, adapter: adaptTextBlock },
  'blocks.cta-block': { component: CtaBlockComponent, adapter: adaptCtaBlock },
  'blocks.table-block': { component: TableBlockComponent, adapter: adaptTableBlock },
  'blocks.callout-block': { component: CalloutBlockComponent, adapter: adaptCalloutBlock },
  'blocks.stats-block': { component: StatsBlockComponent, adapter: adaptStatsBlock },
  'blocks.faq-block': { component: FaqBlockComponent, adapter: adaptFaqBlock },
  'blocks.quote-block': { component: QuoteBlockComponent, adapter: adaptQuoteBlock },
  'blocks.logo-block': { component: LogoBlockComponent, adapter: adaptLogoBlock },
  'blocks.timeline-block': { component: TimelineBlockComponent, adapter: adaptTimelineBlock },
  'blocks.step-block': { component: StepBlockComponent, adapter: adaptStepBlock },
  'blocks.tab-block': { component: TabBlockComponent, adapter: adaptTabBlock },
  'blocks.insights-cta-block': { component: InsightsCtaBlockComponent, adapter: adaptInsightsCtaBlock },
  'blocks.detection-summary-block': { component: DetectionSummaryBlockComponent, adapter: adaptDetectionSummaryBlock },
  'blocks.video-embed': { component: VideoEmbedBlockComponent, adapter: adaptVideoEmbedBlock },
  'blocks.drivetrain-schematic': { component: DrivetrainSchematicBlockComponent, adapter: adaptDrivetrainSchematicBlock },
  'blocks.hubspot-form': { component: HubSpotFormBlockComponent, adapter: adaptHubSpotFormBlock },
  'blocks.raw-html': { component: RawHtmlBlockComponent, adapter: adaptRawHtmlBlock },
};
